import { HttpClient, HttpParams } from "@angular/common/http";
import { Environment, PaginatedList, utils } from "@fabrik/common";
import { UserCreditAccount, CreditTransaction, AddCreditTransaction, CreditTransactionWithSummary } from "../models/Promoter";
import { forkJoin, Observable, of } from "rxjs";
import { TimeUtils } from '../../common/utilities/time-utils'
import { formatDate } from "@angular/common";
import { CreditTransactionReportType } from "../enums/promoter.enum";
import moment from "moment";
import { switchMap, map } from "rxjs/operators";


export class PromoterDataService {
    constructor(private environment: Environment, private http: HttpClient) { }

    /**
     * List promoters
     * @param tenantId id of the current tenant
     * @param args query params for pagination, sorting, etc.
     */
    list(tenantId: string, args: { page?: number; limit?: number; sortField?: string; sortDirection?: string; includeMarketPlaceProfiles?: boolean; query?: string; profileState?: string; } = {}): Observable<PaginatedList<UserCreditAccount>> {
        let params = new HttpParams()
        Object.keys(args).forEach(key => {
            switch (key) {
                case 'page':
                    if (utils.notNullOrUndefined(args.page)) {
                        params = params.set('page', (args.page - 1).toString())
                    }
                    break
                default:
                    if (utils.notNullOrUndefined(args[key])) {
                        params = params.set(key, args[key])
                    }
            }
        })
        return this.http.get<PaginatedList<UserCreditAccount>>(`${this.environment.appApiHost}/api/${tenantId}/admin/userCreditAccounts`, { params });
    }
    getTransactions(tenantId: string, startDate?: Date, endDate?: Date, args: { userId?: string, page?: number; limit?: number; sortField?: string; sortDirection?: string; } = {}): Observable<PaginatedList<CreditTransaction>> {
        let params = new HttpParams()
        Object.keys(args).forEach(key => {
            switch (key) {
                case 'page':
                    if (utils.notNullOrUndefined(args.page)) {
                        params = params.set('page', (args.page - 1).toString())
                    }
                    break
                default:
                    if (utils.notNullOrUndefined(args[key])) {
                        params = params.set(key, args[key])
                    }
            }
        });
        if (startDate && endDate) {            
            let fromDate = new Date(formatDate(startDate, 'E, d MMM yyyy HH:mm:ss Z', 'en'));
            let toDate = new Date(formatDate(endDate, 'E, d MMM yyyy HH:mm:ss Z', 'en'));
            params = params.set('from', TimeUtils.recreateDateAsUTC(fromDate).toISOString())
            params = params.set('to', TimeUtils.recreateDateAsUTC(toDate).toISOString())
        }
        return this.http.get<PaginatedList<CreditTransaction>>(`${this.environment.appApiHost}/api/${tenantId}/admin/userCreditAccounts/transactions`, { params });
    }
    addTransaction(tenantId: string, transaction: AddCreditTransaction){
        return this.http.post<CreditTransaction>(`${this.environment.appApiHost}/api/${tenantId}/admin/userCreditAccounts/adjustment`, transaction);
    }
    setState(tenantId: string, userId:string, userState :string ): Observable<UserCreditAccount> {
        return this.http.post<UserCreditAccount>(`${this.environment.appApiHost}/api/${tenantId}/admin/userCreditAccounts/state?userId=${userId}&userState=${userState}`,{});
    }
    getTenantTransactionsWithSummary(tenantId: string, creditTransactionReportType: CreditTransactionReportType, includeUserDetails: boolean = true, startDate?: Date, endDate?: Date ,args: { page?: number; limit?: number; } = {}): Observable<CreditTransactionWithSummary> {
        let params = new HttpParams()
            .set('creditTransactionReportType', CreditTransactionReportType[creditTransactionReportType])
            .set('includeUserDetails', includeUserDetails.toString());
        Object.keys(args).forEach(key => {
            switch (key) {
                case 'page':
                    if (utils.notNullOrUndefined(args.page)) {
                        params = params.set('page', (args.page - 1).toString())
                    }
                    break
                default:
                    if (utils.notNullOrUndefined(args[key])) {
                        params = params.set(key, args[key])
                    }
            }
        });
        if (startDate && endDate) {
            let fromDate = new Date(formatDate(startDate, 'E, d MMM yyyy HH:mm:ss Z', 'en'));
            let toDate = new Date(formatDate(endDate, 'E, d MMM yyyy HH:mm:ss Z', 'en'));
            params = params.set('from', TimeUtils.recreateDateAsUTC(fromDate).toISOString())
            params = params.set('to', TimeUtils.recreateDateAsUTC(toDate).toISOString())
        }
        return this.http.get<CreditTransactionWithSummary>(`${this.environment.appApiHost}/api/${tenantId}/admin/userCreditAccounts/tenantTransactions`, { params });
    }
    getTenantTransactionsStatement(tenantId: string, includeUserDetails: boolean = true, startDate?: Date, endDate?: Date): Observable<CreditTransactionWithSummary> {
        const allTransactions: CreditTransaction[] = []
        const limit = 20
        let page = 0
        const buildUrl = (currentPage: number): string => {
            const params: string[] = [`tenantId=${tenantId}`, `limit=${limit}`, `page=${currentPage}`, `includeUserDetails=${includeUserDetails.toString()}`,`creditTransactionReportType=${CreditTransactionReportType.ALL}`]
            if (startDate && endDate) {
                params.push(`from=${moment(startDate).utc().toISOString()}`)
                params.push(`to=${moment(endDate).utc().toISOString()}`)
            }
            return `${this.environment.appApiHost}/api/${tenantId}/admin/userCreditAccounts/tenantTransactions?${params.join('&')}`;
        }
        const fetchPage = (currentPage: number): Observable<CreditTransactionWithSummary> => {
            return this.http.get<CreditTransactionWithSummary>(buildUrl(currentPage))
        }
        return fetchPage(page).pipe(
            switchMap((firstResponse) => {
                allTransactions.push(...firstResponse.result)
                const totalPages = Math.ceil(firstResponse.totalCount / limit)
                const remainingPages: Observable<CreditTransactionWithSummary>[] = []
                for (let i = 1; i < totalPages; i++) {
                    remainingPages.push(fetchPage(i))
                }
                if (remainingPages.length === 0) {
                    return of({
                        result: allTransactions,
                        nextPage: null,
                        previousPage: null,
                        totalCount: firstResponse.totalCount,
                        summary: firstResponse.summary
                    })
                }
                return forkJoin(remainingPages).pipe(
                    map((responses) => {
                        responses.forEach((response) => {
                            allTransactions.push(...response.result)
                        })
                        return {
                            result: allTransactions,
                            nextPage: null,
                            previousPage: null,
                            totalCount: firstResponse.totalCount,
                            summary: firstResponse.summary
                        }
                    })
                )
            })
        )
    }

}

